package com.patronat.top.features.dashboard.presentation.ownerreviews

import androidx.lifecycle.viewModelScope
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.ReviewFilterRequest
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.features.dashboard.domain.usecase.GetOwnerReviewsUseCase
import com.patronat.top.features.dashboard.domain.usecase.ReplyToOwnerReviewUseCase
import com.patronat.top.features.dashboard.domain.usecase.command.GetOwnerReviewsCommand
import com.patronat.top.features.dashboard.domain.usecase.command.ReplyToOwnerReviewCommand
import com.patronat.top.features.venues.domain.usecase.GetVenueByIdUseCase
import com.patronat.top.features.venues.domain.usecase.command.GetVenueByIdCommand
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class OwnerReviewsViewModel(
    private val alertController: AlertController,
    private val getOwnerReviewsUseCase: GetOwnerReviewsUseCase,
    private val replyToOwnerReviewUseCase: ReplyToOwnerReviewUseCase,
    private val getVenueByIdUseCase: GetVenueByIdUseCase
) : BaseViewModel<OwnerReviewsState, OwnerReviewsEvent, OwnerReviewsEffect>(
    initialState = OwnerReviewsState()
) {

    private var searchJob: Job? = null

    override fun onEvent(event: OwnerReviewsEvent) {
        when (event) {
            OwnerReviewsEvent.Initialize -> loadData(page = 1)
            OwnerReviewsEvent.OnReload -> loadData(page = currentState.currentPage)
            is OwnerReviewsEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.query) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(500)
                    loadData(page = 1)
                }
            }
            is OwnerReviewsEvent.OnPageChanged -> loadData(page = event.page)
            is OwnerReviewsEvent.OnPageSizeChanged -> {
                updateState { it.copy(pageSize = event.pageSize, currentPage = 1) }
                loadData(page = 1)
            }
            is OwnerReviewsEvent.OnReplyChanged -> {
                updateState { state ->
                    state.copy(replyDrafts = state.replyDrafts + (event.reviewId to event.text))
                }
            }
            is OwnerReviewsEvent.OnSendReplyClicked -> sendReply(event.reviewId)
        }
    }

    private fun loadData(page: Int) {
        val request = ReviewFilterRequest(
            page = page,
            pageSize = currentState.pageSize,
            query = currentState.searchQuery.ifBlank { null }
        )

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { getOwnerReviewsUseCase.execute(GetOwnerReviewsCommand(request)) },
            onSuccess = { response ->
                updateState {
                    it.copy(
                        reviews = response.items,
                        totalCount = response.totalCount,
                        currentPage = response.page,
                        pageSize = response.pageSize,
                        error = null
                    )
                }
                loadVenueNames(response.items)
            },
            onError = { _, message ->
                updateState { it.copy(error = message, reviews = emptyList(), totalCount = 0) }
                handleError(
                    message?.let { UiText.DynamicString(it) }
                        ?: UiText.DynamicString("Не вдалося завантажити відгуки")
                )
            }
        )
    }

    private fun loadVenueNames(reviews: List<ReviewResponse>) {
        val missingVenueIds = reviews
            .map { it.venueId }
            .distinct()
            .filterNot { venueId ->
                currentState.venueNamesById.containsKey(venueId) || currentState.failedVenueIds.contains(venueId)
            }

        missingVenueIds.forEach { venueId ->
            launchNetwork(
                request = { getVenueByIdUseCase.execute(GetVenueByIdCommand(venueId)) },
                onSuccess = { venue ->
                    updateState { state ->
                        state.copy(venueNamesById = state.venueNamesById + (venueId to venue.name))
                    }
                },
                onError = { _, _ ->
                    updateState { state -> state.copy(failedVenueIds = state.failedVenueIds + venueId) }
                }
            )
        }
    }

    private fun sendReply(reviewId: String) {
        if (currentState.replyingReviewId != null) return

        val text = currentState.replyDrafts[reviewId]?.trim().orEmpty()
        if (text.isBlank()) {
            handleError(UiText.DynamicString("Напишіть відповідь перед відправкою"))
            return
        }

        launchNetwork(
            onLoading = { loading ->
                updateState { it.copy(replyingReviewId = if (loading) reviewId else null) }
            },
            request = {
                replyToOwnerReviewUseCase.execute(
                    ReplyToOwnerReviewCommand(
                        reviewId = reviewId,
                        text = text
                    )
                )
            },
            onSuccess = { updatedReview ->
                alertController.show(
                    title = UiText.DynamicString("Відповідь надіслано"),
                    message = UiText.DynamicString("Вона буде відображатися в мобільному застосунку під відгуком."),
                    type = NotificationType.SUCCESS
                )
                updateState { state ->
                    state.copy(
                        reviews = state.reviews.map { review ->
                            if (review.id == updatedReview.id) updatedReview else review
                        },
                        replyDrafts = state.replyDrafts - reviewId
                    )
                }
            },
            onError = { _, message ->
                handleError(
                    message?.let { UiText.DynamicString(it) }
                        ?: UiText.DynamicString("Не вдалося надіслати відповідь")
                )
            }
        )
    }

    private fun handleError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}
